import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Brand, Registration } from '@stax-core/types-omni';
import styled from 'styled-components';

import {
  Button,
  ButtonProps,
  Checkbox,
  Field,
  Form,
  FormDivider,
  Group,
  IqQuestionsModal,
  Modal,
  PageHeader,
  ResponsiveMargin,
  ResponsivePadding,
  VerifyIdentity,
} from '../../components';

import { PrimaryButton } from '@stax-core/truffle-components';
import { Mutator } from 'final-form';
import merge from 'lodash/merge';
import { useLocation } from 'react-router-dom';
import { coreapi } from '../../api';
import {
  AgreementModal,
  ApplicationModal,
} from '../../components/HellosignModal';
import {
  AuthStore,
  BrandStore,
  RegistrationStore,
  updateAuthMerchant,
  updateRegistration,
} from '../../context';
import { history } from '../../history';
import {
  useAreAllFieldsHidden,
  useIdentityVerification,
  useRequiredDocumentGate,
  useToaster,
  useWriteableFieldFormValidation,
} from '../../hooks';
import { parse } from '../../util/api.util';
import { isPayfacMerchant } from '../../util/merchant.util';
import { BusinessFields } from '../business/BusinessFields';
import * as businessFieldsUtil from '../business/BusinessFields.util';
import { Documents } from '../documents/Documents';
import { FinancialFields } from '../financial/FinancialFields';
import * as financialFieldsUtil from '../financial/FinancialFields.util';
import { PaymentsFields } from '../payments/PaymentsFields';
import * as paymentsFieldsUtil from '../payments/PaymentsFields.util';
import { ReviewFormErrors, ReviewFormValues } from './ReviewPage.types';

const Container = styled.div`
  @media (max-width: 1000px) {
    padding: 24px;
  }
  h1 {
    margin: 0;
    padding-left: 0px;
  }
`;

const creditInquiryModalContent = `You understand that by clicking on the checkbox associated with this notice, you are providing ‘written instructions’ to Stax and any lender to whom Stax forwards this application, in connection with this application, for commercial lending under the Fair Credit Reporting Act, hereby authorizing Stax and any such lender to obtain information from your personal credit report or other information from Experian. You authorize Stax and any such lender to obtain such information solely in connection with this application for commercial lending.`;

const LinkButton: FunctionComponent<ButtonProps> = props => (
  <Button variant="link" inline style={{ margin: 0 }} {...props} />
);

const validateForm = (
  formValues: ReviewFormValues,
  areBusinessFieldsHidden: boolean,
  arePaymentsFieldsHidden: boolean,
  areFinancialFieldsHidden: boolean,
  isPayfac: boolean,
  brandName: string,
  brand: Brand,
): ReviewFormErrors => {
  let errors = {} as ReviewFormErrors;

  // only validate the business fields if not hidden
  if (!areBusinessFieldsHidden) {
    errors = merge(
      errors,
      businessFieldsUtil.validateRequiredFormValues(formValues),
      businessFieldsUtil.validateFormValues(formValues),
    );
  }

  // only validate the payments fields if not hidden
  if (!arePaymentsFieldsHidden) {
    errors = merge(
      errors,
      paymentsFieldsUtil.validateRequiredFormValues(formValues, isPayfac),
      paymentsFieldsUtil.validateFormValues(formValues),
    );
  }

  // only validate the Business Owner/Financial fields if not hidden
  if (!areFinancialFieldsHidden) {
    errors = merge(
      errors,
      financialFieldsUtil.validateRequiredFormValues(
        formValues,
        brandName,
        brand,
      ),
      financialFieldsUtil.validateFormValues(formValues),
    );
  }

  if (isPayfac && !formValues.isPayfacAgreementCheckboxChecked) {
    errors.isPayfacAgreementCheckboxChecked = 'Required';
  }

  if (!formValues.isCreditInquiryAgreementCheckboxChecked) {
    errors.isCreditInquiryAgreementCheckboxChecked = 'Required';
  }

  return errors;
};

type ApplicationModalState = 'closed' | 'opening' | 'opened';

export const ReviewPage: FunctionComponent = () => {
  const {
    dispatch: authDispatch,
    state: { auth },
  } = useContext(AuthStore);
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const {
    state: { brand, brandName },
  } = useContext(BrandStore);
  const { toaster, toast } = useToaster();
  const { search } = useLocation();
  const query = parse(search);

  const [isoApplicationModalState, setIsoApplicationModalState] =
    useState<ApplicationModalState>('closed');
  const [isPayfacAgreementModalOpen, setIsPayfacAgreementModalOpen] =
    useState(false);
  const [
    isCreditInquiryAgreementModalOpen,
    setIsCreditInquiryAgreementModalOpen,
  ] = useState(false);

  const [isEarlyAccess, setIsEarlyAccess] = useState(false);
  const [brandRedirectUrl, setBrandRedirectUrl] = useState('');

  // Gate submission on required documents: the merchant can't submit until every
  // `is_required` document has at least one uploaded file (SPX-2051).
  const {
    showRequiredErrors: showRequiredDocErrors,
    handleMissingRequiredChange,
    guardProgression,
  } = useRequiredDocumentGate();

  // Gate submission on identity (step-up) verification when the `release-idology`
  // flag is on and the underwriting service reports step-up is required (SPX-2112).
  const {
    isEnabled: isIdentityVerificationEnabled,
    status: identityVerificationStatus,
    items: identityVerificationItems,
    hasError: identityVerificationError,
    isLoading: isIdentityVerificationLoading,
    guardVerification,
    isModalOpen: isIqModalOpen,
    openModal: openIqModal,
    submitAnswers,
  } = useIdentityVerification();
  const isPayfac = isPayfacMerchant(auth?.merchant);
  const shouldBypassApplicationSigning =
    isPayfac && !brand?.registrationHellosignId;
  const authToken = auth?.token;
  const userId = auth?.user?.id;
  const { regReviewHeader, regReviewBody } = brand || {};

  const isPendingMerchant = auth?.merchant?.status === 'PENDING';
  const allowEarlyAccess = brand?.allowEarlyAccess;
  const redirectLink = brand?.selfRegRedirectUrl;
  const token = auth?.token;

  const onSign = useCallback(async () => {
    const signPayload = {
      credit_inquiry_electronic_signature: {
        html_content: creditInquiryModalContent,
      },
    } as {
      credit_inquiry_electronic_signature: Registration['electronic_signature'];
      electronic_signature?: Registration['electronic_signature'];
    };

    // FOR ISO: if this merchant is iso,
    // then most of the electronic_signature will actually be built out by the underwriting service
    // when it receives the sign webhook event from hellosign
    // however, hellosign does not return the ip address for the signature
    // so in order for the core api to autofill the ip address, we'll do a PUT /team/registration/sign here
    // with the existing electronic_signature built out by the underwriting service spread in

    // combine existing electronic signature and the electronic signature generated by the underwriting service
    // in the case the self-reg front end (this app) calls onSign after underwriting service finishes

    // Note: the reason we're specifying user_id in the payload
    // is in case this onSign function is invoked **before** the underwriting service has a chance to fill the electronic_signature
    // in the case that that happens, we need to have *something* in the electronic_signature payload
    // in order for the core api to notice that electronic_signature is filled so that it then fills in the request ip

    // FOR PAYFAC: if this merchant is payfac,
    // then the rest of the electronic_signature (the electronic_signature.signed_terms_and_conditions) will be built out by the core api
    // but we do still need to send *something* in the electronic_signature payload
    // in order for the core api to notice that electronic_signature is filled so that it then fills in the request ip
    signPayload.electronic_signature = {
      user_id: userId,
      ...(registration?.electronic_signature || {}),
    };
    //Unset the timestamp  -- allow the api to just default to now()
    if (signPayload.electronic_signature?.timestamp) {
      delete signPayload.electronic_signature?.timestamp;
    }
    const r = await coreapi.put(
      authToken!,
      '/team/registration/sign',
      signPayload,
    );

    dispatch(updateRegistration(r));

    // if merchant is pending, application is signed, and brand allows early access, create token and redirect to stax-pay
    // redirect to brand url if it exists
    // otherwise, push to the application tracker (pizza tracker) page
    if (
      isPendingMerchant &&
      allowEarlyAccess &&
      r.signature_status === 'SIGNED'
    ) {
      setIsEarlyAccess(true);
    } else if (redirectLink) {
      setBrandRedirectUrl(redirectLink);
    } else {
      // if the iso application modal was open
      // go ahead and close it
      setIsoApplicationModalState('closed');
      history.push('/tracker');
    }
  }, [
    registration,
    userId,
    authToken,
    dispatch,
    isPendingMerchant,
    allowEarlyAccess,
    redirectLink,
  ]);

  const onSubmit = useCallback(
    async (formValues: ReviewFormValues) => {
      // block submission and reveal inline errors if any required document is missing
      if (!guardProgression()) return;

      // block submission until identity (step-up) verification is complete
      if (!guardVerification()) return;

      const payload: Partial<Registration> = {
        ...businessFieldsUtil.mapFormValuesToPayload(formValues),
        ...financialFieldsUtil.mapFormValuesToPayload(formValues),
        ...paymentsFieldsUtil.mapFormValuesToPayload(formValues),
      };

      if (registration?.meta) {
        payload.meta = {
          // spread in existing registration meta values to save anything not in payload
          ...registration.meta,
          // then spread in any payload meta values to overwrite any duplicate values
          ...payload.meta,
        };
      }

      // Remove files off the body so we don't run into weirdness with adding/removing files
      // This will not remove existing files off a record
      delete payload['files'];

      payload.application_agreed_at = new Date()
        .toISOString()
        .slice(0, 19)
        .replace('T', ' ');

      const teamPayload = {
        ...businessFieldsUtil.mapFormValuesToTeamPayload(formValues),
        ...financialFieldsUtil.mapFormValuesToPayload(formValues),
      };

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));

        const m = await coreapi.put(authToken!, '/team', teamPayload);
        authDispatch(updateAuthMerchant(m));

        if (shouldBypassApplicationSigning) {
          // if this merchant is payfac and has no brand-level Hellosign template ID,
          // the act of checking the "I agree" checkbox and submitting this form
          // is equivalent to an iso merchant signing the hellosign agreement
          // so we can just call onSign here
          onSign();
        } else {
          setIsoApplicationModalState('opening');
        }
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [
      guardVerification,
      authToken,
      authDispatch,
      dispatch,
      shouldBypassApplicationSigning,
      toast,
      toaster,
      onSign,
      registration,
      guardProgression,
    ],
  );

  useEffect(() => {
    if (
      query?.is_pending_re_signature &&
      registration?.signature_status === 'PENDING RE-SIGNATURE' &&
      !shouldBypassApplicationSigning
    ) {
      /**
       * If we receive is_pending_re_signature, and registration is waiting for a resignature,
       * then we open the signature modal
       */
      setIsoApplicationModalState('opening');
    }
  }, [shouldBypassApplicationSigning, query, registration]);

  useEffect(() => {
    if (isEarlyAccess && isoApplicationModalState === 'closed') {
      window.location.href = `${process.env.REACT_APP_OMNI_APP_URL}/#/sso?jwt[]=${token}&building=1`;
    }
  }, [isEarlyAccess, isoApplicationModalState, token]);

  useEffect(() => {
    if (brandRedirectUrl && isoApplicationModalState === 'closed') {
      window.location.href = brandRedirectUrl;
    }
  }, [brandRedirectUrl, isoApplicationModalState]);

  const initialValues = useMemo((): ReviewFormValues => {
    return {
      ...businessFieldsUtil.mapRegistrationToInitialFormValues(registration),
      ...financialFieldsUtil.mapRegistrationToInitialFormValues(registration),
      ...paymentsFieldsUtil.mapRegistrationToInitialFormValues(registration),
      isPayfacAgreementCheckboxChecked: false,
      isCreditInquiryAgreementCheckboxChecked: false,
    };
  }, [registration]);

  const areBusinessFieldsHidden = useAreAllFieldsHidden('Business');
  const arePaymentsFieldsHidden = useAreAllFieldsHidden('Payments');
  const areFinancialFieldsHidden = useAreAllFieldsHidden(
    'Business Owner/Financial',
  );

  const validate = useWriteableFieldFormValidation(validateForm);

  const mutators = {
    ...financialFieldsUtil.formMutators,
    ...paymentsFieldsUtil.formMutators,
  } as any as {
    [key: string]: Mutator<ReviewFormValues, Partial<ReviewFormValues>>;
  };

  return (
    <>
      <PageHeader
        style={{ paddingBottom: '1rem' }}
        id="review-page-header"
        data-testid="review-page-header"
        header={regReviewHeader}
        description={<p>{regReviewBody}</p>}
      />
      <Container>
        <Form<ReviewFormValues>
          id="review-page-form"
          data-testid="review-page-form"
          initialValues={initialValues}
          validate={values => {
            return validate(
              values,
              areBusinessFieldsHidden,
              arePaymentsFieldsHidden,
              areFinancialFieldsHidden,
              isPayfac,
              brandName,
              brand,
            );
          }}
          onSubmit={onSubmit}
          mutators={mutators}
        >
          {formProps => (
            <>
              <Group direction="column" space="1rem/0">
                {areBusinessFieldsHidden ? null : (
                  <BusinessFields mode="review" {...(formProps as any)} />
                )}
                {arePaymentsFieldsHidden ? null : (
                  <PaymentsFields mode="review" {...(formProps as any)} />
                )}
                <div style={{ height: '1rem' }} />
                {areFinancialFieldsHidden ? null : (
                  <FinancialFields
                    mode="review"
                    {...(formProps as any)}
                    principalSignerFooter={
                      isIdentityVerificationEnabled &&
                      identityVerificationStatus !== null ? (
                        <VerifyIdentity
                          status={identityVerificationStatus}
                          hasError={identityVerificationError}
                          onVerify={openIqModal}
                        />
                      ) : null
                    }
                  />
                )}
                <Documents
                  showRequiredErrors={showRequiredDocErrors}
                  onMissingRequiredChange={handleMissingRequiredChange}
                />

                <ResponsiveMargin mobile="1rem 0" desktop="1rem 0">
                  <FormDivider />
                  {isPayfac && (
                    <Group direction="row" style={{ paddingTop: '2rem' }}>
                      <Field
                        name="isPayfacAgreementCheckboxChecked"
                        type="checkbox"
                      >
                        {props => (
                          <Checkbox
                            {...props.input}
                            id="isPayfacAgreementCheckboxChecked"
                            style={{ marginLeft: '0.5rem' }}
                          />
                        )}
                      </Field>

                      <label htmlFor="isPayfacAgreementCheckboxChecked">
                        By submitting your merchant application, you acknowledge
                        that you have received, read, understood and agree to
                        the{' '}
                        <a
                          href="https://staxpayments.com/terms-of-use"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <span>Terms of Use</span>
                        </a>
                        ,{' '}
                        <LinkButton
                          id="payfac-agreement-link"
                          data-testid="payfac-agreement-link"
                          onClick={e => {
                            e.stopPropagation();
                            setIsPayfacAgreementModalOpen(true);
                          }}
                        >
                          <span>Merchant Agreement</span>
                        </LinkButton>{' '}
                        and their associated terms and conditions, and certify
                        that all information you have provided in the Merchant
                        Agreement is complete and correct.
                      </label>
                    </Group>
                  )}
                  <Group direction="row" style={{ paddingTop: '2rem' }}>
                    <Field
                      name="isCreditInquiryAgreementCheckboxChecked"
                      type="checkbox"
                    >
                      {props => (
                        <Checkbox
                          {...props.input}
                          id="isCreditInquiryAgreementCheckboxChecked"
                          style={{ marginLeft: '0.5rem' }}
                        />
                      )}
                    </Field>

                    <label htmlFor="isCreditInquiryAgreementCheckboxChecked">
                      {isPayfac ? (
                        <>
                          You further agree that submitting the Sub-Merchant
                          Agreement constitutes your written authorization to
                          perform certain credit checks as part of the{' '}
                          <LinkButton
                            id="credit-inquiry-agreement-link"
                            data-testid="credit-inquiry-agreement-link"
                            onClick={e => {
                              e.stopPropagation();
                              setIsCreditInquiryAgreementModalOpen(true);
                            }}
                          >
                            <span>FCRA Permissible Purpose Guidelines</span>
                          </LinkButton>
                          .
                        </>
                      ) : (
                        <>
                          You further agree to provide written authorization to
                          undergo a credit check as part of the{' '}
                          <LinkButton
                            id="credit-inquiry-agreement-link"
                            data-testid="credit-inquiry-agreement-link"
                            onClick={e => {
                              e.stopPropagation();
                              setIsCreditInquiryAgreementModalOpen(true);
                            }}
                          >
                            <span>FCRA Permissible Purpose Guidelines</span>
                          </LinkButton>
                          .
                        </>
                      )}
                    </label>
                  </Group>
                </ResponsiveMargin>
                <ResponsivePadding>
                  <Group justify="end">
                    <PrimaryButton
                      style={{ width: '100%' }}
                      type="submit"
                      id="review-submit-button"
                      data-testid="review-submit-button"
                      trackingId="review-submit-button"
                      disabled={
                        formProps.submitting ||
                        (isIdentityVerificationEnabled &&
                          isIdentityVerificationLoading) ||
                        (isPayfac &&
                          !formProps.values.isPayfacAgreementCheckboxChecked) ||
                        !formProps.values
                          .isCreditInquiryAgreementCheckboxChecked
                      }
                    >
                      {shouldBypassApplicationSigning ? (
                        <span>
                          {formProps.submitting ||
                          isoApplicationModalState === 'opening'
                            ? 'Saving...'
                            : 'Submit'}
                        </span>
                      ) : (
                        <span>
                          {formProps.submitting ||
                          isoApplicationModalState === 'opening'
                            ? 'Saving...'
                            : 'Save & Sign'}
                        </span>
                      )}
                    </PrimaryButton>
                  </Group>
                </ResponsivePadding>
              </Group>
              <Modal
                title="FCRA Permissible Purpose Guidelines"
                isOpen={isCreditInquiryAgreementModalOpen}
                onClose={() => setIsCreditInquiryAgreementModalOpen(false)}
              >
                <ResponsivePadding desktop="1rem" mobile="1rem">
                  {creditInquiryModalContent}

                  <Group justify="end">
                    <Button
                      onClick={() =>
                        setIsCreditInquiryAgreementModalOpen(false)
                      }
                    >
                      <span>Close</span>
                    </Button>
                  </Group>
                </ResponsivePadding>
              </Modal>
              <AgreementModal
                formValues={formProps.values}
                isOpen={isPayfacAgreementModalOpen}
                onClose={() => setIsPayfacAgreementModalOpen(false)}
              />
              <IqQuestionsModal
                isOpen={isIqModalOpen}
                questions={identityVerificationItems}
                onSubmit={submitAnswers}
              />
              <ApplicationModal
                formValues={formProps.values}
                applicationType={
                  auth?.merchant?.merchant_processor_application || 'iso'
                }
                isOpen={isoApplicationModalState !== 'closed'}
                onLoad={() => setIsoApplicationModalState('opened')}
                onClose={() => setIsoApplicationModalState('closed')}
                onSign={onSign}
              />
            </>
          )}
        </Form>
      </Container>
    </>
  );
};
